import React from "react";
import type { UseFormSetValue } from "react-hook-form";
import type {
  AddCostFormValues,
  FormulaVariable,
  PlanningMonth,
} from "../../CostPanel.types";
import { decimalPattern } from "./addCostSchema";
import { monthCellWriteOptions } from "./monthCellWrite";

interface PastedCell {
  variableId: string;
  monthKey: string;
  value: string;
}

const rowSeparator = "\n"; // Excel separates rows with a newline
const columnSeparator = "\t"; // ...and columns within a row with a tab
// A grid paste (rather than a single value) carries at least one separator.
const multiCellMarker = new RegExp(`[${rowSeparator}${columnSeparator}]`);

const windowsLineBreak = /\r\n/g; // Windows/Excel line break, normalised to "\n"
const trailingNewline = /\n$/; // Excel's extra newline after the last row

function parseClipboardGrid(text: string): string[][] {
  return text
    .replace(windowsLineBreak, "\n")
    .replace(trailingNewline, "")
    .split(rowSeparator)
    .map((row) => row.split(columnSeparator));
}

function resolvePastedCells(
  block: string[][],
  anchor: { rowIndex: number; colIndex: number },
  perMonthVars: readonly FormulaVariable[],
  planningMonths: readonly PlanningMonth[],
  isValid: (value: string) => boolean,
): PastedCell[] {
  const cells: PastedCell[] = [];

  block.forEach((row, blockRow) => {
    const variable = perMonthVars[anchor.rowIndex + blockRow];
    if (!variable) return;

    row.forEach((raw, blockCol) => {
      const month = planningMonths[anchor.colIndex + blockCol];
      if (!month) return;

      const value = raw.trim();
      if (value === "" || !isValid(value)) return;

      cells.push({ variableId: variable.id, monthKey: month.key, value });
    });
  });

  return cells;
}

type CellPasteHandler = (event: React.ClipboardEvent<HTMLInputElement>) => void;

const useGridPasteHandler = (
  perMonthVars: FormulaVariable[],
  planningMonths: PlanningMonth[],
  setValue: UseFormSetValue<AddCostFormValues>,
): ((rowIndex: number, colIndex: number) => CellPasteHandler) => {
  return React.useCallback(
    (rowIndex: number, colIndex: number): CellPasteHandler =>
      (event) => {
        const text = event.clipboardData.getData("text");
        if (!multiCellMarker.test(text)) return;
        event.preventDefault();

        const cells = resolvePastedCells(
          parseClipboardGrid(text),
          { rowIndex, colIndex },
          perMonthVars,
          planningMonths,
          (value) => decimalPattern.test(value),
        );
        for (const { variableId, monthKey, value } of cells) {
          setValue(`${variableId}.${monthKey}`, value, monthCellWriteOptions);
        }
      },
    [perMonthVars, planningMonths, setValue],
  );
};

export default useGridPasteHandler;
